import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { GetWorkbookResponse } from "@/apis/workbook/types";
import queries from "@/queries";

const APP_TITLE = "Invector for XBRL";

const PAGE_TITLE_BY_PATH = [
  { path: "/workbooks", title: "DSD 워크북 관리" },
  { path: "/compare/dsd-dsd", title: "DSD - DSD 비교" },
  { path: "/ixd-dsd-compare", title: "DSD - IXD 비교" },
  { path: "/ixd-workbook-match", title: "DSD 워크북 - IXD 값 채우기" },
  { path: "/ixd-preview-compare", title: "IXD - IXD 비교" },
  { path: "/ixd-editor", title: "IXD 값 편집기" },
  { path: "/ixd-note-checklist", title: "IXD 주석 검증" },
  { path: "/ixd-auto-input", title: "IXD 자동 입력" },
  { path: "/dsd-editor", title: "DSD 리뷰" },
  { path: "/dsd-xlsx", title: "DSD 엑셀 변환" },
  { path: "/accounting-sheet-dsd", title: "정산표 DSD 변환" },
  { path: "/help/admin", title: "AI 상담 관리" },
  { path: "/help", title: "AI 상담" },
  { path: "/login", title: "로그인" },
] as const;

/** 경로에 해당하는 페이지 이름 ("DSD 리뷰" 등). 없으면 null. 상단 바와 document.title 이 같이 쓴다 */
export const getPageLabel = (pathname: string): string | null =>
  PAGE_TITLE_BY_PATH.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`),
  )?.title ?? null;

const getPageTitle = (pathname: string) => {
  if (pathname === "/") return APP_TITLE;

  const label = getPageLabel(pathname);

  return label ? `${label} | ${APP_TITLE}` : APP_TITLE;
};

// workbook 정보를 받아 동적 타이틀을 만드는 페이지들 (예: "태깅 - 연결 2024 Q4")
const WORKBOOK_TITLE_ROUTES = [
  { pattern: "/mapping/:workbookId/:categoryId", label: "태깅" },
  { pattern: "/preview/:workbookId", label: "최종 산출물" },
] as const;

const FINANCIAL_STATEMENT_LABEL: Record<string, string> = {
  CONSOLIDATED: "연결",
  SEPARATE: "별도",
};

const matchWorkbookTitleRoute = (pathname: string) => {
  for (const route of WORKBOOK_TITLE_ROUTES) {
    const matched = matchPath(route.pattern, pathname);
    if (matched) {
      return { label: route.label, workbookId: matched.params.workbookId };
    }
  }
  return null;
};

// "연결 2024 Q4" 형태. 값이 없는 항목은 생략한다.
const buildWorkbookTitleSuffix = (workbook?: GetWorkbookResponse): string => {
  if (!workbook) return "";

  const statementType = workbook.financialStatementType
    ? (FINANCIAL_STATEMENT_LABEL[workbook.financialStatementType] ?? "")
    : "";

  return [statementType, workbook.reportYear, workbook.reportQuarter]
    .filter((part) => part !== null && part !== undefined && part !== "")
    .join(" ");
};

export const DocumentTitle = () => {
  const location = useLocation();
  const workbookRoute = matchWorkbookTitleRoute(location.pathname);
  const workbookId = workbookRoute?.workbookId;
  const label = workbookRoute?.label;

  const { data: workbook } = useQuery({
    ...queries.workbook(workbookId ?? ""),
    enabled: !!workbookId,
  });

  useEffect(() => {
    if (label) {
      const suffix = buildWorkbookTitleSuffix(workbook);
      document.title = `${label}${suffix ? ` - ${suffix}` : ""} | ${APP_TITLE}`;
    } else {
      document.title = getPageTitle(location.pathname);
    }
  }, [location.pathname, label, workbook]);

  return null;
};
